import { Folder, Plus } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { PageHeader } from '@/components/common/PageHeader';
import { QueryErrorView } from '@/components/common/QueryErrorView';
import { Can } from '@/components/permission/Can';
import { DataTable, type DataTableColumn } from '@/components/shared/DataTable';
import { EmptyState } from '@/components/shared/EmptyState';
import { LoadingRows } from '@/components/shared/LoadingRows';
import { Button } from '@/components/ui/button';
import { PERMISSION_ACTIONS, PROJECT_RESOURCE } from '@/features/permissions/model';
import { useCan } from '@/features/permissions/useCan';
import { useGetOrgProjectsQuery } from '@/features/orgs/api/orgApi';
import { OrgDetailHeader } from '@/features/orgs/components/OrgDetailHeader';
import type { OrgProject } from '@/features/orgs/types';
import { formatDate } from '@/features/common/format';
import { CreateProjectDialog } from '../components/CreateProjectDialog';

/**
 * Projects tab (`/organizations/:orgId/projects`, design §11).
 *
 * Data is the org-scoped list (`GET /organizations/:id/projects`) — `GET
 * /projects` is deliberately unused in P1 because projects live under an org.
 * “New project” is hidden unless `projects:create` is allowed (§14.5), and the
 * row (whole click target) opens the project detail.
 */
export function OrgProjectsPage() {
  const { orgId = '' } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const projectsQuery = useGetOrgProjectsQuery(orgId);
  const canCreate = useCan(PERMISSION_ACTIONS.projectCreate, PROJECT_RESOURCE);
  const [createOpen, setCreateOpen] = useState(false);

  const projects = projectsQuery.data ?? [];

  const createButton = (
    <Button onClick={() => setCreateOpen(true)}>
      <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
      New project
    </Button>
  );
  // Hidden unless `projects:create` is allowed (§14.5 entry-point rule).
  const projectAction = canCreate.allowed === true ? createButton : undefined;

  const columns: DataTableColumn<OrgProject>[] = [
    {
      id: 'name',
      header: 'Name',
      cell: (project) => <span className="font-medium">{project.name ?? '—'}</span>,
    },
    {
      id: 'createdAt',
      header: 'Created',
      cell: (project) => (
        <span className="text-muted-foreground">{formatDate(project.createdAt)}</span>
      ),
    },
    {
      id: 'actions',
      header: '',
      srHeader: 'Actions',
      className: 'text-right',
      headerClassName: 'text-right',
      cell: () => (
        <span className="text-sm text-muted-foreground" aria-hidden="true">
          Open →
        </span>
      ),
    },
  ];

  return (
    <div>
      <OrgDetailHeader orgId={orgId} />

      <section aria-labelledby="projects-title">
        <PageHeader
          title="Projects"
          titleAs="h2"
          titleId="projects-title"
          actions={
            <Can action={PERMISSION_ACTIONS.projectCreate} resource={PROJECT_RESOURCE}>
              {createButton}
            </Can>
          }
        />

        {projectsQuery.isLoading ? (
          <LoadingRows label="Loading projects…" columns={3} />
        ) : projectsQuery.isError ? (
          <QueryErrorView
            error={projectsQuery.error}
            label="projects"
            onRetry={() => void projectsQuery.refetch()}
          />
        ) : (
          <DataTable
            columns={columns}
            data={projects}
            pageSize={10}
            getRowId={(project) => project.id ?? project._id ?? project.name ?? ''}
            onRowClick={(project) => {
              const id = project.id ?? project._id;
              if (id) {
                navigate(`/organizations/${orgId}/projects/${id}`);
              }
            }}
            emptyState={
              <EmptyState
                icon={<Folder />}
                title="No projects yet"
                description="Create the first project for this organization."
                action={projectAction}
              />
            }
          />
        )}
      </section>

      <CreateProjectDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        orgId={orgId}
        onCreated={(projectId) => navigate(`/organizations/${orgId}/projects/${projectId}`)}
      />
    </div>
  );
}
